import { useCallback, useEffect, useState } from 'react';

// 실시간 작업 위젯에서 "인지 완료" 된 jobId 를 브라우저 단위로 숨기기 위한 localStorage 저장소.
// 권위: UX 결정 — 운영자는 종료된(completed / failed / stale) 작업을 수동으로 dismiss 할 수 있다.
// running 상태 작업은 dismiss 해도 widget 이 다시 노출하므로, 호출부에서 가드한다.
//
// Storage 형식:
//   { "<jobId>": <unix_ms_dismissed_at>, ... }
// PURGE_MS 이상 지난 항목은 매 마운트마다 정리해 무한 성장 방지.

const STORAGE_KEY = 'alphaomega.dismissed_jobs';
const PURGE_MS = 14 * 24 * 60 * 60 * 1000; // 14일

// 여러 훅 인스턴스가 동시에 존재해도 같은 Set 을 공유하도록 모듈 스코프 state + 구독자 리스트 사용.
// (tab 간 sync 는 storage 이벤트로 보조.)
type DismissedMap = Record<string, number>;

let sharedState: DismissedMap = loadInitial();
const subscribers = new Set<(next: DismissedMap) => void>();

function loadInitial(): DismissedMap {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const now = Date.now();
    const cleaned: DismissedMap = {};
    for (const [id, ts] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof ts !== 'number') continue;
      if (now - ts > PURGE_MS) continue;
      cleaned[id] = ts;
    }
    // 정리된 결과를 다시 저장 (purge 반영).
    if (Object.keys(cleaned).length !== Object.keys(parsed).length) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
      } catch {
        /* quota 초과 등은 무시 */
      }
    }
    return cleaned;
  } catch {
    return {};
  }
}

function persist(next: DismissedMap): void {
  sharedState = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  for (const cb of subscribers) cb(next);
}

// 다른 탭에서의 변경 동기화.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    sharedState = loadInitial();
    for (const cb of subscribers) cb(sharedState);
  });
}

export interface DismissedJobsApi {
  /** jobId 가 dismiss 상태인지 여부. */
  isDismissed: (jobId: string) => boolean;
  /** 단일 jobId 를 dismiss 로 기록. */
  dismiss: (jobId: string) => void;
  /** 복수 jobId 를 한 번에 dismiss 로 기록 ("모두 인지"). */
  dismissMany: (jobIds: string[]) => void;
  /** dismiss 상태를 해제 (테스트/복원 용도, UI 에서는 현재 미사용). */
  undismiss: (jobId: string) => void;
  /** 내부 상태를 그대로 노출 — 필터 로직에서 참조. */
  dismissedSet: ReadonlySet<string>;
}

export function useDismissedJobs(): DismissedJobsApi {
  const [snapshot, setSnapshot] = useState<DismissedMap>(sharedState);

  useEffect(() => {
    subscribers.add(setSnapshot);
    return () => {
      subscribers.delete(setSnapshot);
    };
  }, []);

  const isDismissed = useCallback(
    (jobId: string) => Object.prototype.hasOwnProperty.call(snapshot, jobId),
    [snapshot]
  );

  const dismiss = useCallback((jobId: string) => {
    if (!jobId) return;
    if (Object.prototype.hasOwnProperty.call(sharedState, jobId)) return;
    persist({ ...sharedState, [jobId]: Date.now() });
  }, []);

  const dismissMany = useCallback((jobIds: string[]) => {
    if (!jobIds.length) return;
    const now = Date.now();
    const next = { ...sharedState };
    let changed = false;
    for (const id of jobIds) {
      if (!id) continue;
      if (Object.prototype.hasOwnProperty.call(next, id)) continue;
      next[id] = now;
      changed = true;
    }
    if (changed) persist(next);
  }, []);

  const undismiss = useCallback((jobId: string) => {
    if (!Object.prototype.hasOwnProperty.call(sharedState, jobId)) return;
    const next = { ...sharedState };
    delete next[jobId];
    persist(next);
  }, []);

  const dismissedSet = new Set(Object.keys(snapshot));

  return { isDismissed, dismiss, dismissMany, undismiss, dismissedSet };
}

// 테스트 전용 — storage 를 강제로 비운다.
export function __resetDismissedJobsForTest(): void {
  sharedState = {};
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
  for (const cb of subscribers) cb(sharedState);
}
